import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Check, ChevronDown, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

/**
 * Campo único com autocompletar: digite e a lista de hospitais cadastrados vai
 * filtrando (nome, sigla, apelidos). Texto livre é permitido (hospital_id null)
 * com aviso — o backend ainda tenta reconhecer nome/sigla/apelido.
 * value: { hospital_id, hospital_name }
 */
export default function HospitalPicker({ value, onChange, testid = "hospital-picker", placeholder = "Digite o nome ou a sigla do hospital…" }) {
  const [hospitals, setHospitals] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/hospitals").then((r) => setHospitals((r.data || []).filter((h) => h.active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const text = value?.hospital_name || "";
  const selected = hospitals.find((h) => h.id === value?.hospital_id);
  const q = norm(text);
  const filtered = useMemo(() => {
    if (!q || selected) return hospitals;
    return hospitals.filter((h) =>
      norm(h.name).includes(q) || norm(h.short_name).includes(q) || (h.name_aliases || []).some((a) => norm(a).includes(q)));
  }, [hospitals, q, selected]);

  function pick(h) {
    onChange({ hospital_id: h.id, hospital_name: h.name });
    setOpen(false);
  }
  function type(e) {
    onChange({ hospital_id: null, hospital_name: e.target.value });
    setOpen(true); setActive(0);
  }
  function onKey(e) {
    if (!open && ["ArrowDown", "ArrowUp"].includes(e.key)) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && filtered[active]) { e.preventDefault(); pick(filtered[active]); }
    else if (e.key === "Escape") setOpen(false);
  }

  const unlinked = !!text && !selected && hospitals.length > 0;

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Building2 className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={text}
          onChange={type}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          className={`pl-9 pr-9 ${selected ? "border-emerald-400 bg-emerald-50/40" : ""}`}
          data-testid={`${testid}-input`}
        />
        <button type="button" tabIndex={-1} onClick={() => setOpen((o) => !o)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          data-testid={`${testid}-toggle`} aria-label="Abrir lista de hospitais">
          {selected ? <Check className="h-4 w-4 text-emerald-600" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <ul role="listbox" data-testid={`${testid}-list`}
          className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg text-sm py-1">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-slate-500" data-testid={`${testid}-empty`}>
              Nenhum hospital cadastrado com “{text}”. Você pode manter o nome digitado.
            </li>
          )}
          {filtered.map((h, i) => (
            <li key={h.id} role="option" aria-selected={h.id === value?.hospital_id}
              onMouseDown={(e) => { e.preventDefault(); pick(h); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${i === active ? "bg-blue-50" : ""} ${h.id === value?.hospital_id ? "font-medium text-blue-800" : "text-slate-800"}`}
              data-testid={`${testid}-option-${h.id}`}>
              <span className="truncate">{h.name}</span>
              <span className="flex items-center gap-2 shrink-0">
                {h.short_name && <span className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{h.short_name}</span>}
                {h.id === value?.hospital_id && <Check className="h-3.5 w-3.5 text-blue-700" />}
              </span>
            </li>
          ))}
        </ul>
      )}

      {unlinked && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700" data-testid={`${testid}-unlinked-warning`}>
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>Hospital não cadastrado — o mapa será salvo com este nome e o sistema tentará reconhecer nome/sigla.
            Cadastre-o em <strong>Hospitais</strong> para a produção/financeiro agruparem por hospital (vínculo automático).</span>
        </p>
      )}
    </div>
  );
}
